import { createFileRoute, Link } from "@tanstack/react-router";
import { AlertTriangle, Check, Mail } from "lucide-react";
import { useEffect, useRef } from "react";
import { useUnsubscribe } from "@/api/mutations/auth-flows";
import { Button } from "@/components/ui/Button";
import { Card } from "@/components/ui/Card";
import { Spinner } from "@/components/ui/Spinner";
import { ApiError } from "@/lib/api-client";

export const Route = createFileRoute("/_public/mail/unsubscribe/$customer/$type/$token")({
	component: UnsubscribePage,
});

function UnsubscribePage() {
	const { customer, type, token } = Route.useParams();
	const hasTriggered = useRef(false);

	const unsubscribeMutation = useUnsubscribe();

	// biome-ignore lint/correctness/useExhaustiveDependencies: One-time effect mit ref guard
	useEffect(() => {
		if (!hasTriggered.current) {
			hasTriggered.current = true;
			unsubscribeMutation.mutate({ customer, type, token });
		}
	}, []);

	if (unsubscribeMutation.isPending) {
		return (
			<div className="flex flex-col items-center gap-4 py-20">
				<Spinner size="lg" label="Abmeldung wird verarbeitet" />
				<p className="text-text-secondary text-base">Einen Moment bitte...</p>
			</div>
		);
	}

	if (unsubscribeMutation.isError) {
		const message =
			unsubscribeMutation.error instanceof ApiError
				? unsubscribeMutation.error.message
				: "Die Abmeldung konnte nicht verarbeitet werden.";

		return (
			<div className="max-w-md mx-auto py-20">
				<Card padding="lg">
					<div className="flex flex-col items-center gap-4 text-center">
						<div className="flex h-16 w-16 items-center justify-center rounded-full bg-error/20">
							<AlertTriangle size={32} className="text-error" />
						</div>
						<h1 className="text-xl font-bold text-text">Abmeldung fehlgeschlagen</h1>
						<p className="text-base text-text-secondary">{message}</p>
						<Link to="/">
							<Button variant="primary">Zur Startseite</Button>
						</Link>
					</div>
				</Card>
			</div>
		);
	}

	if (unsubscribeMutation.isSuccess) {
		return (
			<div className="max-w-md mx-auto py-20">
				<Card padding="lg">
					<div className="flex flex-col items-center gap-4 text-center">
						<div className="flex h-16 w-16 items-center justify-center rounded-full bg-success/20">
							<Check size={32} className="text-success" />
						</div>
						<h1 className="text-xl font-bold text-text">Erfolgreich abgemeldet</h1>
						<p className="text-base text-text-secondary">
							Du wurdest erfolgreich von diesen E-Mail-Benachrichtigungen abgemeldet.
						</p>
						<div className="flex h-12 w-12 items-center justify-center rounded-full bg-surface-dim">
							<Mail size={24} className="text-text-muted" />
						</div>
						<Link to="/">
							<Button variant="primary">Zur Startseite</Button>
						</Link>
					</div>
				</Card>
			</div>
		);
	}

	return null;
}
